import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronRight,
  BookOpen,
  Calendar,
  Users,
  ClipboardList,
  Video,
  FileText,
  BarChart2,
  Clock,
  Layout,
  Activity,
  Home,
  Radio,
  Target,
} from "lucide-react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { getCoursesById } from "../../../services/course.service";
import { useCourse } from "../../../context/CourseContext";
import { useMeetingsV2 } from "../../../context/MeetingV2Context";
import { useUtilityContext } from "../../../context/UtilityContext";
import {
  pageTitle,
  bodySecondary,
  accentBarSection,
  microLabel,
  card,
  surfaceMuted,
  motionFadeUp,
  motionPageTransition,
  motionHoverLift,
  emptyState,
} from "../../../lib/design-system";
import { cn } from "../../../lib/utils";
import { Skeleton, SkeletonCard } from "../../StudentDashboard/Components/ui/Skeleton";
import ComingSoonWrapper from "../../../components/ComingSoonWrapper";

// Import content components
import CourseBrief from "./course/CourseBrief";
import WeeklyPlanManager from "./course/WeeklyPlanner";
import StudentTable from "./course/StudentDetail";
import AllAssignments from "../../Assignment/teacher/AllAssignments";
import LectureReview from "./course/LectureReview";
import SyllabusManager from "./course/Syllabus";
import CourseSchedule from "./course/CourseSchedule";
import AttendanceHeatMap from "./course/AttendenceHeatMap";
import AttendanceTracker from "./course/AttendanceTracker";
import AttendanceStats from "./course/AttendanceStats";
import Gradebook from "./course/Gradebook";
import TeacherHome from "./course/TeacherHome";
import AnnouncementManagement from "./course/AnnouncementManagement";
import ContentSection from "./course/Content/Content";
import SelfQuiz from "./course/Test/SelfQuiz";
import QuizCreator from "./course/Test/QuizCreator";
import DiscussionForum from "./course/Discussion";
import AllActivities from "../../Activity/teacher/AllActivities";
import BlogCreator from "./course/Blog/BlogCreator";
import ContinuousAssessment from "./course/ContinuousAssessment";
// Phase 2d teacher integration — reuses the admin-side outcomes view.
// Same component works for both because the backend's verifyCourseAccess
// helper enforces "admin OR course-owning teacher" at the endpoint level.
import CourseOutcomesView from "../../admin/Components/Programs/CourseOutcomesView";

// ---------------------------------------------------------------------------
// NAV CONFIG
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  { key: "Home", label: "Overview", icon: Home },
  {
    key: "course",
    label: "Course",
    icon: BookOpen,
    children: [
      { key: "Course Brief", label: "Brief", icon: FileText },
      { key: "Syllabus", label: "Syllabus", icon: BookOpen },
      { key: "Outcomes", label: "Outcomes", icon: Target },
      { key: "Weekly Plan", label: "Weekly Plan", icon: Calendar },
      { key: "Class Schedule", label: "Schedule", icon: Clock },
    ],
  },
  {
    key: "students",
    label: "Students",
    icon: Users,
    children: [
      { key: "Mark Attendance", label: "Attendance", icon: ClipboardList },
      { key: "Heat Map", label: "Heat Map", icon: BarChart2 },
      { key: "Status Sheet", label: "Status Sheet", icon: Layout },
      { key: "Class List", label: "Class List", icon: Users },
    ],
  },
  {
    key: "assessment",
    label: "Evaluation",
    icon: Activity,
    children: [
      { key: "All Assignments", label: "Assignments", icon: ClipboardList },
      { key: "Activity", label: "Activities", icon: Activity, comingSoon: true },
      { key: "Continuous Assessment", label: "Internal Marks", icon: BarChart2 },
    ],
  },
  { key: "Recorded Lectures", label: "Recordings", icon: Video },
  { key: "Content", label: "Content", icon: FileText },
  { key: "Gradebook", label: "Grades", icon: BarChart2 },
  { key: "Discussion", label: "Discuss", icon: FileText },
  { key: "Announcements", label: "Announcements", icon: FileText },
];

// ---------------------------------------------------------------------------
// BREADCRUMB (memoised)
// ---------------------------------------------------------------------------

const Breadcrumb = React.memo(({ courseTitle, onNavigate }) => (
  <motion.div
    {...motionFadeUp}
    className={cn(surfaceMuted, "px-4 sm:px-6 py-3 rounded-sm flex items-center gap-2 text-sm")}
  >
    <button
      onClick={() => onNavigate("Dashboard")}
      className="text-gray-500 dark:text-gray-400 hover:text-accent1 font-medium transition-colors"
    >
      Dashboard
    </button>
    <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
    <button
      onClick={() => onNavigate("myCourses")}
      className="text-gray-500 dark:text-gray-400 hover:text-accent1 font-medium transition-colors"
    >
      My Courses
    </button>
    <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
    <span className="text-gray-900 dark:text-white font-bold truncate max-w-[200px] sm:max-w-none">
      {courseTitle}
    </span>
  </motion.div>
));
Breadcrumb.displayName = "Breadcrumb";

// ---------------------------------------------------------------------------
// TAB STRIP (memoised)
// ---------------------------------------------------------------------------

const TabStrip = React.memo(({ activeTab, onTabChange }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (openDropdown) {
        const ref = dropdownRefs.current[openDropdown];
        if (ref && !ref.contains(e.target)) setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openDropdown]);

  const isChildActive = (item) =>
    item.children?.some((c) => c.key === activeTab);

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 relative">
      {/* Desktop tabs */}
      <div className="hidden md:flex items-center gap-1 px-4 sm:px-6">
        {NAV_ITEMS.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isActive = activeTab === item.key || isChildActive(item);

          if (!hasChildren) {
            return (
              <button
                key={item.key}
                onClick={() => onTabChange(item.key)}
                className={cn(
                  "relative px-4 py-3 whitespace-nowrap transition-colors",
                  microLabel,
                  isActive
                    ? "text-accent1"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                )}
              >
                {item.label}
                {isActive && (
                  <motion.div
                    layoutId="teacher-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-1 bg-accent1 rounded-sm"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
              </button>
            );
          }

          // Dropdown tab
          return (
            <div
              key={item.key}
              className="relative"
              ref={(el) => (dropdownRefs.current[item.key] = el)}
            >
              <button
                onClick={() =>
                  setOpenDropdown(openDropdown === item.key ? null : item.key)
                }
                className={cn(
                  "relative flex items-center gap-1.5 px-4 py-3 whitespace-nowrap transition-colors",
                  microLabel,
                  isActive
                    ? "text-accent1"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                )}
              >
                {item.label}
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform",
                    openDropdown === item.key && "rotate-180"
                  )}
                />
                {isActive && (
                  <motion.div
                    layoutId="teacher-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-1 bg-accent1 rounded-sm"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
              </button>

              {openDropdown === item.key && (
                <div
                  className={cn(
                    card,
                    "absolute left-0 top-full mt-1 w-48 py-1 z-50"
                  )}
                >
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const isComingSoon = child.comingSoon;
                    return (
                      <ComingSoonWrapper
                        key={child.key}
                        isComingSoon={isComingSoon}
                        featureName={child.label}
                        estimatedRelease="Coming Soon"
                        onClick={() => {
                          if (!isComingSoon) {
                            onTabChange(child.key);
                            setOpenDropdown(null);
                          }
                        }}
                      >
                        <button
                          onClick={() => {
                            if (!isComingSoon) {
                              onTabChange(child.key);
                              setOpenDropdown(null);
                            }
                          }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                            activeTab === child.key
                              ? "bg-accent1/10 text-accent1 font-semibold"
                              : "text-gray-700 dark:text-gray-300 font-medium",
                            !isComingSoon && "hover:bg-gray-50 dark:hover:bg-gray-700"
                          )}
                        >
                          <ChildIcon className="h-4 w-4 flex-shrink-0" />
                          {child.label}
                        </button>
                      </ComingSoonWrapper>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile tabs — horizontally scrollable */}
      <div className="md:hidden flex items-center gap-1 px-3 overflow-x-auto scrollbar-hide">
        {NAV_ITEMS.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isActive = activeTab === item.key || isChildActive(item);

          if (!hasChildren) {
            return (
              <button
                key={item.key}
                onClick={() => onTabChange(item.key)}
                className={cn(
                  "relative flex-shrink-0 px-3 py-3 whitespace-nowrap transition-colors text-xs uppercase tracking-wider font-bold",
                  isActive
                    ? "text-accent1"
                    : "text-gray-500 dark:text-gray-400"
                )}
              >
                {item.label}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-accent1 rounded-sm" />
                )}
              </button>
            );
          }

          return (
            <div
              key={item.key}
              className="relative flex-shrink-0"
              ref={(el) => (dropdownRefs.current[`mobile-${item.key}`] = el)}
            >
              <button
                onClick={() =>
                  setOpenDropdown(
                    openDropdown === `mobile-${item.key}` ? null : `mobile-${item.key}`
                  )
                }
                className={cn(
                  "relative flex items-center gap-1 px-3 py-3 whitespace-nowrap transition-colors text-xs uppercase tracking-wider font-bold",
                  isActive
                    ? "text-accent1"
                    : "text-gray-500 dark:text-gray-400"
                )}
              >
                {item.label}
                <ChevronDown
                  className={cn(
                    "h-3 w-3 transition-transform",
                    openDropdown === `mobile-${item.key}` && "rotate-180"
                  )}
                />
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-accent1 rounded-sm" />
                )}
              </button>

              {openDropdown === `mobile-${item.key}` && (
                <div
                  className={cn(
                    card,
                    "absolute left-0 top-full mt-1 w-44 py-1 z-50"
                  )}
                >
                  {item.children.map((child) => {
                    const isComingSoon = child.comingSoon;
                    return (
                      <ComingSoonWrapper
                        key={child.key}
                        isComingSoon={isComingSoon}
                        featureName={child.label}
                        estimatedRelease="Coming Soon"
                        onClick={() => {
                          if (!isComingSoon) {
                            onTabChange(child.key);
                            setOpenDropdown(null);
                          }
                        }}
                      >
                        <button
                          onClick={() => {
                            if (!isComingSoon) {
                              onTabChange(child.key);
                              setOpenDropdown(null);
                            }
                          }}
                          className={cn(
                            "w-full text-left px-4 py-2.5 text-sm transition-colors",
                            activeTab === child.key
                              ? "bg-accent1/10 text-accent1 font-semibold"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          )}
                        >
                          {child.label}
                        </button>
                      </ComingSoonWrapper>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
TabStrip.displayName = "TabStrip";

// ---------------------------------------------------------------------------
// SKELETON LOADER
// ---------------------------------------------------------------------------

const CourseManagementSkeleton = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="p-4 sm:p-6 space-y-6">
      <Skeleton className="h-10 w-full max-w-md" />
      <div className="space-y-3">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-1 w-12" />
      </div>
      <div className="flex gap-4">
        {[...Array(7)].map((_, i) => (
          <Skeleton key={i} className="h-10 w-24" />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SkeletonCard className="h-64" />
        <SkeletonCard className="h-64" />
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

const CourseManagement = () => {
  const { courseID: urlCourseID } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCourseId, setActiveSection } = useUtilityContext();

  // Support both inline mode (context) and standalone route (URL params)
  const courseID = urlCourseID || selectedCourseId;

  const getStoredSection = (courseId) => {
    if (!courseId) return "Home";
    const storageKey = `course_${courseId}_selectedSection`;
    const stored = localStorage.getItem(storageKey);
    return stored || "Home";
  };

  const [selectedOption, setSelectedOption] = useState(() => getStoredSection(courseID));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update selectedOption when courseID changes
  useEffect(() => {
    if (courseID) {
      const stored = getStoredSection(courseID);
      setSelectedOption(stored);
    }
  }, [courseID]);

  // Listen for section change events from child components
  useEffect(() => {
    const handleSectionChange = (event) => {
      if (event.detail && event.detail.section) {
        setSelectedOption(event.detail.section);
      }
    };

    window.addEventListener('sectionChange', handleSectionChange);
    return () => {
      window.removeEventListener('sectionChange', handleSectionChange);
    };
  }, []);

  // Save selectedOption to localStorage whenever it changes
  useEffect(() => {
    if (courseID && selectedOption) {
      const storageKey = `course_${courseID}_selectedSection`;
      localStorage.setItem(storageKey, selectedOption);
    }
  }, [selectedOption, courseID]);

  // New meetings context (backend-driven)
  const { getMeetingsForCourse, fetchMeetingsForCourse } = useMeetingsV2();

  // Load meetings for this course from backend
  useEffect(() => {
    if (courseID) {
      fetchMeetingsForCourse(courseID).catch(() => {});
    }
  }, [courseID, fetchMeetingsForCourse]);

  const courseMeetings = getMeetingsForCourse(courseID) || [];

  // Keep current time updated
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Find live meeting instance for this course
  const liveMeeting = useMemo(() => {
    if (!courseID || !courseMeetings.length) return null;
    return courseMeetings.find(
      (meeting) =>
        meeting.course === courseID &&
        meeting.status === "live"
    );
  }, [courseID, courseMeetings]);

  const { courseData, setCourseData, markSessionsAsSaved } = useCourse();

  const handleJoinLiveClass = useCallback(() => {
    if (liveMeeting) {
      window.open(liveMeeting.link, '_blank', 'noopener,noreferrer');
    } else {
      alert("There is no live class to join at the moment.");
    }
  }, [liveMeeting]);

  const extractCourseIdFromPath = () => {
    const pathParts = location.pathname.split("/");
    return pathParts[pathParts.length - 1];
  };

  // Fetch course data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      const courseId = courseID || extractCourseIdFromPath();

      if (!courseId) {
        setLoading(false);
        setError("No course ID found in the URL");
        return;
      }

      try {
        setLoading(true);
        const response = await getCoursesById(courseId);

        const normalizedSyllabus = (() => {
          if (!response?.syllabus) return { modules: [] };
          let modules = [];
          if (Array.isArray(response.syllabus)) {
            modules = response.syllabus;
          } else if (Array.isArray(response.syllabus.modules)) {
            modules = response.syllabus.modules;
          }
          modules = modules.map(module => ({
            ...module,
            topics: Array.isArray(module.topics) ? module.topics : []
          }));
          return { modules };
        })();

        setCourseData({ ...response, syllabus: normalizedSyllabus });

        const sessionsArr = Array.isArray(response.attendance?.sessions) ? response.attendance.sessions : [];
        const savedSessionKeys = sessionsArr.map((s) => {
          const dateStr = (s.date || "").includes("T") ? s.date.split("T")[0] : s.date;
          return `${dateStr}_${s.time || ""}_${s.branch || ""}`;
        });
        if (savedSessionKeys.length > 0 && markSessionsAsSaved) {
          markSessionsAsSaved(savedSessionKeys);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching course data:", err);
        setError("Failed to load course data. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
  }, [courseID, setCourseData]);

  const handleTabChange = useCallback((tab) => {
    setSelectedOption(tab);
  }, []);

  const handleBreadcrumbNavigate = useCallback(
    (section) => {
      // Inline mode: navigate via context
      if (!urlCourseID && setActiveSection) {
        setActiveSection(section);
      } else {
        // Standalone route: use router
        navigate(-1);
      }
    },
    [urlCourseID, setActiveSection, navigate]
  );

  const renderContent = () => {
    return (
      <div className="bg-white dark:bg-gray-900 w-full relative">
        <div className="min-h-96">
          {selectedOption === "Course Brief" && <CourseBrief />}
          {selectedOption === "Weekly Plan" && <WeeklyPlanManager />}
          {selectedOption === "Class List" && <StudentTable />}
          {selectedOption === "Subjective" && (
            <AllAssignments courseID={courseID} initialTab="subjective" hideTabs={true} />
          )}
          {selectedOption === "Objective" && (
            <AllAssignments courseID={courseID} initialTab="objective" hideTabs={true} />
          )}
          {selectedOption === "All Assignments" && (
            <AllAssignments courseID={courseID} />
          )}
          {selectedOption === "Activity" && (
            <AllActivities courseID={courseID} />
          )}
          {selectedOption === "Home" && (
            <TeacherHome setSelectedOption={setSelectedOption} />
          )}
          {selectedOption === "Recorded Lectures" && <LectureReview />}
          {selectedOption === "Syllabus" && <SyllabusManager />}
          {selectedOption === "Outcomes" && (
            <CourseOutcomesView
              courseId={courseID}
              courseCode={courseData?.courseCode}
            />
          )}
          {selectedOption === "Class Schedule" && <CourseSchedule />}
          {selectedOption === "Heat Map" && <AttendanceHeatMap />}
          {selectedOption === "Mark Attendance" && <AttendanceTracker />}
          {selectedOption === "Status Sheet" && <AttendanceStats />}
          {selectedOption === "Content" && (
            <ContentSection setSelectedOption={setSelectedOption} />
          )}
          {selectedOption === "Self Test" && <SelfQuiz />}
          {selectedOption === "Create Quiz" && <QuizCreator />}
          {selectedOption === "Announcements" && (
            <AnnouncementManagement courseID={courseID} />
          )}
          {selectedOption === "Gradebook" && <Gradebook courseID={courseID} />}
          {selectedOption === "Continuous Assessment" && <ContinuousAssessment />}
          {selectedOption === "Discussion" && <DiscussionForum />}
        </div>
      </div>
    );
  };

  // Loading state — skeleton
  if (loading) {
    return <CourseManagementSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className={emptyState}>
          <BookOpen className="h-10 w-10 text-red-400 dark:text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-red-600 dark:text-red-400 mb-2">Error</h2>
          <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Breadcrumb */}
      <div className="px-4 sm:px-6 pt-4">
        <Breadcrumb
          courseTitle={courseData.title || "Course"}
          onNavigate={handleBreadcrumbNavigate}
        />
      </div>

      {/* Course Header */}
      <motion.div
        {...motionFadeUp}
        className="px-4 sm:px-6 pt-4 pb-2"
      >
        <div className="flex items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className={pageTitle}>{courseData.title}</h1>
            <p className={cn(bodySecondary, "mt-1")}>
              {courseData.teacher?.name}
            </p>
            <div className={cn(accentBarSection, "mt-3")} />
          </div>

          {/* Live class button */}
          {liveMeeting ? (
            <motion.button
              whileHover={motionHoverLift}
              onClick={handleJoinLiveClass}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm bg-accent1 text-white font-bold uppercase tracking-wider text-xs shadow-sm hover:shadow-md transition-shadow"
            >
              <Radio className="h-4 w-4 animate-pulse" />
              <span className="hidden sm:inline">Join Live Class</span>
              <span className="sm:hidden">Join Live</span>
            </motion.button>
          ) : (
            <button
              disabled
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider text-xs cursor-not-allowed"
            >
              <Radio className="h-4 w-4" />
              <span className="hidden sm:inline">No Live Class</span>
            </button>
          )}
        </div>
      </motion.div>

      {/* Tab Strip */}
      <div className="px-4 sm:px-6 pt-2 relative z-30">
        <TabStrip activeTab={selectedOption} onTabChange={handleTabChange} />
      </div>

      {/* Tab Content */}
      <div className="relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedOption}
            {...motionPageTransition}
            className="py-4"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CourseManagement;
